import axios from "axios";
import { logger } from "./tslog";
import { getSlideNumberById } from "@/utils/placeholderSelection";

export async function sendSpeakerNotesToExtension(
  slideId: string,
  speakerNotesText: string
): Promise<boolean> {
  try {
    if (!slideId || !speakerNotesText || !speakerNotesText.trim()) {
      logger.warn("[PowerPointExtension] Cannot send speaker notes: missing slideId or empty text", {
        hasSlideId: !!slideId,
        hasText: !!(speakerNotesText && speakerNotesText.trim()),
      });
      return false;
    }

    // Get slide number from slideId
    const slideNumber = await getSlideNumberById(slideId);
    
    if (!slideNumber || slideNumber < 1) {
      logger.warn("[PowerPointExtension] Cannot send speaker notes: invalid slide number", {
        slideId,
        slideNumber,
      });
      return false;
    }

    // Get extension base URL
    const extensionBaseUrl = process.env.POWERPOINT_EXTENTION_URL as string;
    const extensionUrl = `${extensionBaseUrl}/insert-speaker-notes`;
    
    logger.info("[PowerPointExtension] Sending speaker notes to extension", {
      slideId,
      slideNumber,
      extensionUrl,
      textLength: speakerNotesText.length,
    });

    // Send POST request to PowerPoint Extension
    await axios.post(
      extensionUrl,
      {
        slideId: slideNumber,
        speakerNotesText: speakerNotesText.trim(),
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000, // 10 second timeout
      }
    );
    
    logger.info("[PowerPointExtension] Successfully sent speaker notes to extension", {
      slideId,
      slideNumber,
      textLength: speakerNotesText.length,
    });
    
    return true;
  } catch (error) {
    logger.error("[PowerPointExtension] Failed to send speaker notes to extension", {
      error: (error as any)?.message || String(error),
      slideId,
      response: (error as any)?.response?.data,
      status: (error as any)?.response?.status,
    });
    return false;
  }
}

export async function sendBulletPointsToExtension(
  slideId: string,
  shapeId: string,
  text: string,
    dimensions: {
      height: number;
      width: number;
      top: number;
      left: number;
    }
): Promise<boolean> {
  try {
    if (!slideId || !shapeId || !text || !text.trim() || !dimensions) {
      logger.warn("[PowerPointExtension] Cannot send bullet points: missing required parameters or dimensions", {
        hasSlideId: !!slideId,
        hasShapeId: !!shapeId,
        hasText: !!(text && text.trim()),
        hasDimensions: !!dimensions,
      });
      return false;
    }

    // Get slide number from slideId
    const slideNumber = await getSlideNumberById(slideId);
    const { height, width, top, left } = dimensions;
    if (!slideNumber || slideNumber < 1) {
      logger.warn("[PowerPointExtension] Cannot send bullet points: invalid slide number", {
        slideId,
        slideNumber,
      });
      return false;
    }

    // Get extension base URL
    const extensionBaseUrl = process.env.POWERPOINT_EXTENTION_URL as string;
    const extensionUrl = `${extensionBaseUrl}/insert-bullet-points`;
    
    // Build request payload
    const payload: any = {
      slideId: slideNumber,
      shapeId: shapeId,
      text: text.trim(),
    };

    // Add optional dimensions if provided
    if (height !== undefined && height !== null) {
      payload.height = height;
    }
    if (width !== undefined && width !== null) {
      payload.width = width;
    }
    if (top !== undefined && top !== null) {
      payload.top = top;
    }
    if (left !== undefined && left !== null) {
      payload.left = left;
    }
    
    logger.info("[PowerPointExtension] Sending bullet points to extension", {
      slideId,
      slideNumber,
      shapeId,
      extensionUrl,
      textLength: text.length,
      hasDimensions: !!(height || width || top || left),
    });

    // Send POST request to PowerPoint Extension
    await axios.post(
      extensionUrl,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000, // 10 second timeout
      }
    );
    
    logger.info("[PowerPointExtension] Successfully sent bullet points to extension", {
      slideId,
      slideNumber,
      shapeId,
      textLength: text.length,
    });
    
    return true;
  } catch (error) {
    logger.error("[PowerPointExtension] Failed to send bullet points to extension", {
      error: (error as any)?.message || String(error),
      slideId,
      shapeId,
      response: (error as any)?.response?.data,
      status: (error as any)?.response?.status,
    });
    return false;
  }
}
